/**
 * Ticket Description Expand/Collapse Toggle
 *
 * Shows a "Mehr lesen" / "Weniger" button below ticket descriptions
 * that overflow the 3-line CSS clamp. Reuses smoothExpand/smoothCollapse
 * from form-helpers.js for consistent animation.
 */

// Safe fallback: __ may not be available during module parse
const __ = (typeof window.__ === 'function')
  ? window.__
  : function(message, replacements) {
    const text = String(message == null ? '' : message);
    if (!Array.isArray(replacements) || replacements.length === 0) return text;
    return text.replace(/\{(\d+)\}/g, (match, idx) => {
      const value = replacements[Number(idx)];
      return value === undefined || value === null ? match : String(value);
    });
  };

const COLLAPSED_LINES = 3;
const LINE_HEIGHT = 1.6;
const CLAMP_HEIGHT_EM = COLLAPSED_LINES * LINE_HEIGHT; // 4.8em
const OVERFLOW_BUFFER_PX = 4; // subpixel rounding tolerance

/**
 * Measure whether description content overflows the 3-line clamp.
 * Temporarily removes clamp to get true scrollHeight.
 *
 * @param {HTMLElement} content - The [data-desc-content] element
 * @returns {boolean}
 */
function isOverflowing(content) {
  if (!content) return false;

  // Save current inline styles
  const saved = {
    display: content.style.display,
    webkitLineClamp: content.style.webkitLineClamp,
    webkitBoxOrient: content.style.webkitBoxOrient,
    maxHeight: content.style.maxHeight,
    overflow: content.style.overflow,
  };

  // Temporarily remove clamp to measure full height
  content.style.display = 'block';
  content.style.webkitLineClamp = 'unset';
  content.style.webkitBoxOrient = '';
  content.style.maxHeight = 'none';
  content.style.overflow = 'visible';

  const fullHeight = content.scrollHeight;

  // Restore
  content.style.display = saved.display;
  content.style.webkitLineClamp = saved.webkitLineClamp;
  content.style.webkitBoxOrient = saved.webkitBoxOrient;
  content.style.maxHeight = saved.maxHeight;
  content.style.overflow = saved.overflow;

  // Calculate clamp threshold in px
  const fontSize = parseFloat(window.getComputedStyle(content).fontSize) || 15;
  const clampHeightPx = CLAMP_HEIGHT_EM * fontSize;

  return fullHeight > clampHeightPx + OVERFLOW_BUFFER_PX;
}

/**
 * Collapse description to 3-line clamp.
 *
 * @param {HTMLElement} wrapper
 * @param {HTMLElement} content
 * @param {HTMLElement} toggle
 */
function collapseDescription(wrapper, content, toggle) {
  const fontSize = parseFloat(window.getComputedStyle(content).fontSize) || 15;
  const targetHeight = CLAMP_HEIGHT_EM * fontSize;

  // Set current height explicitly for transition start
  content.style.maxHeight = `${content.scrollHeight}px`;
  content.style.overflow = 'hidden';

  requestAnimationFrame(() => {
    content.style.maxHeight = `${targetHeight}px`;
    content.style.opacity = '1';
  });

  let cleaned = false;
  const cleanup = (e) => {
    if (cleaned) return;
    if (e && e.propertyName !== 'max-height') return;
    cleaned = true;
    content.removeEventListener('transitionend', cleanup);

    // Apply CSS clamp for clean ellipsis
    content.style.display = '-webkit-box';
    content.style.webkitLineClamp = String(COLLAPSED_LINES);
    content.style.webkitBoxOrient = 'vertical';
    content.style.maxHeight = '';
    content.style.overflow = 'hidden';
  };

  content.addEventListener('transitionend', cleanup);
  setTimeout(cleanup, 350); // Safety buffer over 250ms CSS transition (--checkout-anim-normal)

  wrapper.classList.remove('checkout-desc--expanded');
  wrapper.classList.add('checkout-desc--collapsed');
  toggle.setAttribute('aria-expanded', 'false');

  const text = toggle.querySelector('[data-desc-toggle-text]');
  if (text) text.textContent = __('Mehr lesen');

  // Rotate chevron icon back to collapsed state
  const chevron = toggle.querySelector('[data-desc-chevron]');
  if (chevron) chevron.classList.remove('checkout-desc-chevron--expanded');
}

/**
 * Expand description to full height.
 *
 * @param {HTMLElement} wrapper
 * @param {HTMLElement} content
 * @param {HTMLElement} toggle
 */
function expandDescription(wrapper, content, toggle) {
  // Remove clamp to measure full height
  content.style.display = 'block';
  content.style.webkitLineClamp = 'unset';
  content.style.webkitBoxOrient = '';
  content.style.overflow = 'hidden';

  const fullHeight = content.scrollHeight;

  // Set collapsed height as starting point
  const fontSize = parseFloat(window.getComputedStyle(content).fontSize) || 15;
  content.style.maxHeight = `${CLAMP_HEIGHT_EM * fontSize}px`;

  requestAnimationFrame(() => {
    content.style.maxHeight = `${fullHeight}px`;
  });

  let cleaned = false;
  const cleanup = (e) => {
    if (cleaned) return;
    if (e && e.propertyName !== 'max-height') return;
    cleaned = true;
    content.removeEventListener('transitionend', cleanup);

    // Remove inline styles so content flows naturally
    content.style.maxHeight = '';
    content.style.overflow = '';
  };

  content.addEventListener('transitionend', cleanup);
  setTimeout(cleanup, 350); // Safety buffer over 250ms CSS transition (--checkout-anim-normal)

  wrapper.classList.remove('checkout-desc--collapsed');
  wrapper.classList.add('checkout-desc--expanded');
  toggle.setAttribute('aria-expanded', 'true');

  const text = toggle.querySelector('[data-desc-toggle-text]');
  if (text) text.textContent = __('Weniger');

  // Rotate chevron icon to expanded state
  const chevron = toggle.querySelector('[data-desc-chevron]');
  if (chevron) chevron.classList.add('checkout-desc-chevron--expanded');
}

/**
 * Initialize description toggles within a host element.
 *
 * @param {HTMLElement} host - Scoped container (e.g. section#tickets)
 */
export function initDescriptionToggles(host) {
  if (!host) return;

  const wrappers = host.querySelectorAll('[data-desc-wrapper]');

  wrappers.forEach((wrapper) => {
    // Guard against double-init
    if (wrapper.dataset.descInit === '1') return;
    wrapper.dataset.descInit = '1';

    const content = wrapper.querySelector('[data-desc-content]');
    const toggle = wrapper.querySelector('[data-desc-toggle]');
    if (!content || !toggle) return;

    // Check overflow after render
    requestAnimationFrame(() => {
      if (isOverflowing(content)) {
        // Start collapsed with toggle visible
        wrapper.classList.add('checkout-desc--collapsed');
        content.style.display = '-webkit-box';
        content.style.webkitLineClamp = String(COLLAPSED_LINES);
        content.style.webkitBoxOrient = 'vertical';
        content.style.overflow = 'hidden';
        toggle.classList.remove('hidden');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        // No overflow: full text, no toggle
        wrapper.classList.add('checkout-desc--expanded');
        toggle.classList.add('hidden');
      }
    });

    // Click handler
    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const isExpanded = wrapper.classList.contains('checkout-desc--expanded');

      if (isExpanded) {
        // Save scroll position before collapse to prevent jarring scroll jumps
        const wrapperRect = wrapper.getBoundingClientRect();
        const scrollBefore = window.scrollY;

        collapseDescription(wrapper, content, toggle);

        // After collapse, if wrapper moved above viewport, adjust scroll
        requestAnimationFrame(() => {
          const wrapperRectAfter = wrapper.getBoundingClientRect();
          const delta = wrapperRectAfter.top - wrapperRect.top;
          if (delta < -10) {
            window.scrollTo({ top: scrollBefore + delta, behavior: 'instant' });
          }
        });
      } else {
        expandDescription(wrapper, content, toggle);
      }
    });

    // Keyboard support: Enter/Space
    toggle.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle.click();
      }
    });
  });
}

/**
 * Re-check overflow on resize (orientation change, window resize).
 *
 * @param {HTMLElement} host
 */
export function recheckDescriptionOverflow(host) {
  if (!host) return;

  host.querySelectorAll('[data-desc-wrapper]').forEach((wrapper) => {
    const content = wrapper.querySelector('[data-desc-content]');
    const toggle = wrapper.querySelector('[data-desc-toggle]');
    if (!content || !toggle) return;

    const wasCollapsible = !toggle.classList.contains('hidden');
    const overflows = isOverflowing(content);

    if (overflows && !wasCollapsible) {
      // Became overflowing - show toggle, collapse
      wrapper.classList.remove('checkout-desc--expanded');
      wrapper.classList.add('checkout-desc--collapsed');
      content.style.display = '-webkit-box';
      content.style.webkitLineClamp = String(COLLAPSED_LINES);
      content.style.webkitBoxOrient = 'vertical';
      content.style.overflow = 'hidden';
      toggle.classList.remove('hidden');
      toggle.setAttribute('aria-expanded', 'false');
      const text = toggle.querySelector('[data-desc-toggle-text]');
      if (text) text.textContent = __('Mehr lesen');
    } else if (!overflows && wasCollapsible) {
      // No longer overflows - hide toggle, show full
      wrapper.classList.remove('checkout-desc--collapsed');
      wrapper.classList.add('checkout-desc--expanded');
      content.style.display = '';
      content.style.webkitLineClamp = '';
      content.style.webkitBoxOrient = '';
      content.style.overflow = '';
      content.style.maxHeight = '';
      toggle.classList.add('hidden');
    }
  });
}
